import { type MutableRefObject, useEffect } from 'react'
import { TextNode } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { type EffectiveTextStyle } from '@backend/lib/db/typeRoots/layoutNode'
import { $createRichTextNode, $isRichTextNode } from './RichTextNode'

type Props = {
  fallbackStyleRef: MutableRefObject<EffectiveTextStyle>
}

/** Paste, native edits, and IME composition can insert base TextNodes that
 *  bypass our RichTextNode subclass. The runs reader only recognizes
 *  RichTextNode, so a plain TextNode is silently dropped on round-trip and
 *  the pasted text vanishes when edit mode exits. This transform replaces
 *  any base TextNode with a RichTextNode that inherits style from the
 *  nearest sibling RichTextNode, falling back to the node's effective style
 *  when the paragraph has none. */
export const NormalizePlainTextPlugin = ({ fallbackStyleRef }: Props) => {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if ($isRichTextNode(node)) return
      const style = nearestRunStyle(node) ?? fallbackStyleRef.current
      node.replace($createRichTextNode(node.getTextContent(), style))
    })
  }, [editor, fallbackStyleRef])
  return null
}

const nearestRunStyle = (node: TextNode): EffectiveTextStyle | null => {
  let cursor = node.getPreviousSibling()
  while (cursor) {
    if ($isRichTextNode(cursor)) return cursor.getRunStyle()
    cursor = cursor.getPreviousSibling()
  }
  cursor = node.getNextSibling()
  while (cursor) {
    if ($isRichTextNode(cursor)) return cursor.getRunStyle()
    cursor = cursor.getNextSibling()
  }
  return null
}
